import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

// Width of the glow at its widest point, when the svg doesn't name its own.
// The markers themselves are 10px wide on desktop (r 4.25 + 1.5 stroke).
const DEFAULT_PING_SIZE = 20

const createPing = (marker, radius) => {
    const ping = document.createElementNS(SVG_NAMESPACE, 'circle')

    ping.setAttribute('data-map-ping', '')
    ping.setAttribute('cx', marker.getAttribute('cx'))
    ping.setAttribute('cy', marker.getAttribute('cy'))
    ping.setAttribute('r', radius)

    // bg-highlight/50, as an inline style so the theme variable resolves
    ping.style.fill = 'var(--color-highlight)'
    ping.style.fillOpacity = '0.5'
    ping.style.pointerEvents = 'none'

    // Behind the marker itself
    marker.parentNode.insertBefore(ping, marker)

    return ping
}

/**
 * Puts a pulsing glow behind every circle of the map svgs inside
 * `[data-map-pings]`, so the markers catch the eye. Each svg can set its own
 * glow width with `data-map-ping-size`, which is how the mobile map gets a
 * smaller one than the desktop map.
 *
 * @param {Document|Element|string} [root] Where to look for maps.
 */
export const initMapPings = (root = document) => {
    const rootEl =
        typeof root === 'string' ? document.querySelector(root) : root

    if (!rootEl) return

    rootEl.querySelectorAll('[data-map-pings]').forEach((mapEl) => {
        if (mapEl.dataset.mapPingsInitialized === 'true') return

        const pings = []

        mapEl.querySelectorAll('svg').forEach((svgEl) => {
            const size =
                Number(
                    svgEl.closest('[data-map-ping-size]')?.dataset.mapPingSize
                ) || DEFAULT_PING_SIZE
            const radius = size / 2

            svgEl
                .querySelectorAll('circle:not([data-map-ping])')
                .forEach((marker) => {
                    pings.push({ el: createPing(marker, radius), radius })
                })
        })

        if (!pings.length) return

        mapEl.dataset.mapPingsInitialized = 'true'

        const pingEls = pings.map((ping) => ping.el)

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            gsap.set(pingEls, { opacity: 1 })
            return
        }

        gsap.set(pingEls, { opacity: 0 })

        ScrollTrigger.create({
            trigger: mapEl,
            start: 'top 85%',
            once: true,
            onEnter: () => {
                // A tween per ping, each with its own duration, so they drift
                // apart instead of breathing in lockstep forever.
                pings.forEach(({ el, radius }, index) => {
                    // Animating `r` instead of scaling: a circle grows around
                    // its own cx/cy, so nothing can push it off centre.
                    gsap.fromTo(
                        el,
                        { opacity: 0, attr: { r: radius * 0.5 } },
                        {
                            opacity: 1,
                            attr: { r: radius },
                            duration: gsap.utils.random(1.2, 1.8),
                            delay: index * 0.1,
                            ease: 'sine.inOut',
                            repeat: -1,
                            yoyo: true
                        }
                    )
                })
            }
        })
    })
}
